import { FunctionComponent, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalyticChartPlatform } from '../../../../services';
import { TabItem, Tabs } from '../../../../shared';
import { ChartType } from '../Analytics';
import { useChartExclude } from '../helpers';
import { UserActive } from './UserActive';
import { UserActivity } from './UserActivity';
import { UserActivityRate } from './UserActivityRate';
import { UserAllActivity } from './UserAllActivity';
import { UserDemoActivity } from './UserDemoActivity';
import { UserDemoFunnel } from './UserDemoFunnel';
import { UserEngagement } from './UserEngagement';
import { UserFunnel } from './UserFunnel';
import { UserFunnelV2 } from './UserFunnelV2';
import { UserFunnelV3 } from './UserFunnelV3';
import { UserInteraction } from './UserInteraction';
import { UserRoleContent } from './UserRoleContent';
import { UserSessionByDay } from './UserSessionByDay';
import { UserSessionByHour } from './UserSessionByHour';
import { UserSessionPerDay } from './UserSessionPerDay';

import classes from '../Analytics.module.scss';

enum AnalyticsUserTabType {
  USER_1,
  USER_2,
  USER_3,
}

export interface UserProps {
  from: string;
  to: string;
  platform?: AnalyticChartPlatform[];
}

export const User: FunctionComponent<UserProps> = memo((userProps) => {
  const { t: ctxT } = useTranslation('', { keyPrefix: 'analyticsUserTabs' });

  const [selectedTabIndex, setSelectedTabIndex] = useState(AnalyticsUserTabType.USER_1);

  const excludeUserAllActivity = useChartExclude(ChartType.ACTIVE_VS_ENGAGED_ALL_USERS);
  const excludeUserActivity = useChartExclude(ChartType.ACTIVE_VS_ENGAGED_USERS);
  const excludeUserActivityRate = useChartExclude(ChartType.ACTIVE_VS_ENGAGED_USERS_RATE);
  const excludeUserDemoActivity = useChartExclude(ChartType.ACTIVE_VS_ENGAGED_DEMO_USERS);
  const excludeUserFunnel = useChartExclude(ChartType.USER_FUNNEL);
  const excludeUserFunnelV2 = useChartExclude(ChartType.USER_FUNNEL_V2);
  const excludeUserFunnelV3 = useChartExclude(ChartType.USER_FUNNEL_V3);
  const excludeUserDemoFunnel = useChartExclude(ChartType.USER_DEMO_FUNNEL);
  const excludeUserInteraction = useChartExclude(ChartType.USER_INTERACTIONS);
  const excludeUserRoleContent = useChartExclude(ChartType.USER_ROLE_CONTENT);
  const excludeUserEngagement = useChartExclude(ChartType.SESSIONS_DURATION);
  const excludeUserSessionPerDay = useChartExclude(ChartType.SESSIONS_PER_DAY);
  const excludeUserSessionByDay = useChartExclude(ChartType.SESSIONS_BY_DAY_OF_WEEK);
  const excludeUserSessionByHour = useChartExclude(ChartType.SESSIONS_BY_HOUR_OF_DAY);
  const excludeUserActive = useChartExclude(ChartType.ACTIVE_USERS_PER_MONTH);

  const tabItems: TabItem[] = useMemo(
    () => [
      {
        index: AnalyticsUserTabType.USER_1,
        name: ctxT('user-1'),
        content: (
          <div className={classes['analytics__tab-content']}>
            {!excludeUserAllActivity && <UserAllActivity {...userProps} />}
            {!excludeUserActivity && <UserActivity {...userProps} />}
            {!excludeUserActivityRate && <UserActivityRate {...userProps} />}
            {!excludeUserDemoActivity && <UserDemoActivity {...userProps} />}
            {!excludeUserFunnel && <UserFunnel {...userProps} />}
            {!excludeUserFunnelV2 && <UserFunnelV2 {...userProps} />}
            {!excludeUserFunnelV3 && <UserFunnelV3 {...userProps} />}
            {!excludeUserDemoFunnel && <UserDemoFunnel {...userProps} />}
            {!excludeUserActive && <UserActive />}
          </div>
        ),
      },
      {
        index: AnalyticsUserTabType.USER_2,
        name: ctxT('user-2'),
        content: (
          <div className={classes['analytics__tab-content']}>
            {!excludeUserInteraction && <UserInteraction {...userProps} />}
            {!excludeUserRoleContent && <UserRoleContent {...userProps} />}
          </div>
        ),
      },
      {
        index: AnalyticsUserTabType.USER_3,
        name: ctxT('user-3'),
        content: (
          <div className={classes['analytics__tab-content']}>
            {!excludeUserEngagement && <UserEngagement {...userProps} />}
            {!excludeUserSessionPerDay && <UserSessionPerDay {...userProps} />}
            {!excludeUserSessionByDay && <UserSessionByDay {...userProps} />}
            {!excludeUserSessionByHour && <UserSessionByHour {...userProps} />}
          </div>
        ),
      },
    ],
    [
      ctxT,
      excludeUserActive,
      excludeUserActivity,
      excludeUserActivityRate,
      excludeUserAllActivity,
      excludeUserDemoActivity,
      excludeUserDemoFunnel,
      excludeUserEngagement,
      excludeUserFunnel,
      excludeUserFunnelV2,
      excludeUserFunnelV3,
      excludeUserInteraction,
      excludeUserRoleContent,
      excludeUserSessionByDay,
      excludeUserSessionByHour,
      excludeUserSessionPerDay,
      userProps,
    ]
  );

  return (
    <Tabs
      items={tabItems}
      selectedTabIndex={selectedTabIndex}
      setSelectedTabIndex={setSelectedTabIndex}
      noBackground
    />
  );
});
